import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ArrowUpRight, 
  Code2, 
  Calendar, 
  ChevronLeft, 
  ChevronRight,
  Maximize2
} from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState('todos');

  const easePremium = [0.22, 1, 0.36, 1];

  const categories = [
    { id: 'todos', label: 'Todos' },
    { id: 'sistemas', label: 'Sistemas Empresariales' },
    { id: 'apps', label: 'Aplicaciones Móviles' },
    { id: 'apis', label: 'APIs y Backends' },
    { id: 'contabilidad', label: 'Soluciones Contables' }
  ];

  const images = [
    { 
      src: '/images/sistema/gastro1.png', 
      alt: 'Sistema de Gestión para Restaurantes', 
      category: 'sistemas',
      title: 'GastroManager Pro',
      description: 'Sistema completo de gestión con control de inventario, mesas y pedidos en tiempo real.',
      technologies: ['React', 'Node.js', 'MongoDB'],
      year: '2024'
    },
    { 
      src: '/images/sistema/gastro2.png', 
      alt: 'Panel de Control Restaurantes', 
      category: 'sistemas',
      title: 'Dashboard GastroManager',
      description: 'Panel de control analítico para la gestión integral de establecimientos gastronómicos.',
      technologies: ['React', 'Socket.io', 'Chart.js'],
      year: '2024'
    },
    { 
      src: '/images/sistema/gastro3.png', 
      alt: 'App Móvil para Restaurantes', 
      category: 'apps',
      title: 'GastroManager Mobile',
      description: 'Aplicación móvil para la gestión ágil de pedidos y control de mesas desde cualquier dispositivo.',
      technologies: ['React Native', 'Firebase'],
      year: '2024'
    },
    { 
      src: '/images/sistema/wasapi.png', 
      alt: 'Plataforma Wasapi', 
      category: 'sistemas',
      title: 'Wasapi Platform',
      description: 'Plataforma integral de automatización de marketing y comunicación empresarial unificada.',
      technologies: ['Vue.js', 'Laravel', 'MySQL'],
      year: '2023'
    },
    { 
      src: '/images/sistema/wasapi0.jpeg', 
      alt: 'Panel de Control Wasapi', 
      category: 'sistemas',
      title: 'Dashboard Wasapi',
      description: 'Panel administrativo con métricas avanzadas y control total de campañas de marketing.',
      technologies: ['Vue.js', 'Laravel', 'Redis'],
      year: '2023'
    },
    { 
      src: '/images/sistema/app_wasapi2.jpeg', 
      alt: 'APP Wasapi Móvil', 
      category: 'apps',
      title: 'Wasapi Mobile App',
      description: 'Aplicación móvil nativa para la gestión de campañas y comunicación en tiempo real.',
      technologies: ['React Native', 'Firebase'],
      year: '2023'
    },
    { 
      src: '/images/sistema/panel_api.png', 
      alt: 'Visioner7 API', 
      category: 'apis',
      title: 'Visioner7 API Gateway',
      description: 'Panel de administración centralizado para la gestión segura de APIs y microservicios.',
      technologies: ['React', 'Express.js', 'MongoDB'],
      year: '2024'
    },
    { 
      src: '/images/sistema/panel_api1.png', 
      alt: 'API Management', 
      category: 'apis',
      title: 'API Management System',
      description: 'Sistema robusto de gestión, monitoreo y limitación de tasa para APIs empresariales.',
      technologies: ['Node.js', 'Express', 'JWT'],
      year: '2024'
    },
    { 
      src: '/images/sistema/app_factus1.jpeg', 
      alt: 'APP Factus', 
      category: 'contabilidad',
      title: 'Factus Mobile App',
      description: 'Aplicación móvil especializada en gestión contable y emisión de facturación electrónica.',
      technologies: ['Flutter', 'Dart', 'SQLite'],
      year: '2023'
    },
    { 
      src: '/images/sistema/transport.png', 
      alt: 'Sistema de Transporte', 
      category: 'sistemas',
      title: 'Transport Management',
      description: 'Sistema integral de gestión de flotas y operaciones para empresas de transporte.',
      technologies: ['Angular', 'Spring Boot', 'PostgreSQL'],
      year: '2023'
    }
  ];

  const filteredImages = activeFilter === 'todos' 
    ? images 
    : images.filter(img => img.category === activeFilter);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredImages.length);
    setSelectedImage(filteredImages[(currentIndex + 1) % filteredImages.length]);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
    setSelectedImage(filteredImages[(prev - 1 + filteredImages.length) % filteredImages.length]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage) {
        if (e.key === 'Escape') setSelectedImage(null);
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex, filteredImages]);

  const openImage = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  return (
    <section id="galeria" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      
      {/* Textura de fondo ultra-sutil (Grid técnico) */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.02]"
        style={{
          backgroundImage: 'linear-gradient(#059669 1px, transparent 1px), linear-gradient(90deg, #059669 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10">
        
        {/* ========================================== */}
        {/* ENCABEZADO CENTRADO                        */}
        {/* ========================================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: easePremium }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-4 uppercase tracking-wider">
            <Code2 className="w-4 h-4" />
            Portafolio de Proyectos
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Soluciones que generan <br className="hidden md:block" />
            <span className="text-emerald-600">impacto real</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Explora una selección de nuestros trabajos más recientes. 
            Cada proyecto refleja nuestro compromiso con la excelencia técnica y los resultados de negocio.
          </p>
        </motion.div>

        {/* ========================================== */}
        {/* FILTROS MINIMALISTAS                       */}
        {/* ========================================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.6, ease: easePremium }}
          className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12 md:mb-16"
        >
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => { setActiveFilter(category.id); setCurrentIndex(0); }}
              className={`px-4 md:px-5 py-2 md:py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeFilter === category.id
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-slate-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </motion.div>

        {/* ========================================== */}
        {/* GRID DE PROYECTOS                          */}
        {/* ========================================== */}
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredImages.map((image, index) => (
              <motion.article
                key={`${image.src}-${index}`}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: easePremium }}
                onClick={() => openImage(image, index)}
                className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-900/5 transition-all duration-300 cursor-pointer flex flex-col"
              >
                {/* Contenedor de Imagen */}
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  
                  {/* Overlay sutil al hover */}
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 bg-white/90 backdrop-blur-sm text-slate-900 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 shadow-lg">
                      Ver proyecto <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </div>

                  {/* Badge de categoría minimalista */}
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-slate-700 border border-slate-200 shadow-sm">
                    {categories.find(c => c.id === image.category)?.label}
                  </div>
                </div>

                {/* Contenido de la Tarjeta */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{image.year}</span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-slate-900 mb-2 leading-snug group-hover:text-emerald-700 transition-colors">
                    {image.title}
                  </h3>
                  
                  <p className="text-sm text-slate-600 leading-relaxed mb-5 line-clamp-2 flex-grow">
                    {image.description}
                  </p>

                  {/* Tecnologías */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
                    {image.technologies.map((tech, i) => (
                      <span key={i} className="bg-slate-50 text-slate-600 text-xs px-2.5 py-1 rounded-md border border-slate-200 font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Mensaje sin resultados */}
        {filteredImages.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 bg-slate-50 rounded-2xl border border-slate-200 border-dashed"
          >
            <Code2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay proyectos en esta categoría</h3>
            <p className="text-slate-600 text-sm">Selecciona "Todos" para ver el portafolio completo.</p>
          </motion.div>
        )}
      </div>

      {/* ========================================== */}
      {/* MODAL DE PROYECTO (Estilo Premium)         */}
      {/* ========================================== */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 md:p-8"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.98 }}
              transition={{ duration: 0.3, ease: easePremium }}
              className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header del Modal */}
              <div className="flex justify-between items-center p-4 md:p-6 border-b border-slate-200 bg-white">
                <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">
                  {categories.find(c => c.id === selectedImage.category)?.label}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 mr-2 hidden sm:block">
                    {currentIndex + 1} / {filteredImages.length}
                  </span>
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Contenido Scrollable */}
              <div className="overflow-y-auto custom-scrollbar">
                {/* Imagen Principal */}
                <div className="relative bg-slate-100 w-full aspect-video md:aspect-[21/9]">
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    className="w-full h-full object-contain md:object-cover"
                  />
                </div>

                {/* Detalles del Proyecto */}
                <div className="p-6 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-3">
                        {selectedImage.title}
                      </h2>
                      <p className="text-slate-600 leading-relaxed max-w-2xl">
                        {selectedImage.description}
                      </p>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2 text-sm text-slate-500 bg-slate-50 px-4 py-2 rounded-lg border border-slate-200">
                      <Calendar className="w-4 h-4" />
                      <span>Año: {selectedImage.year}</span>
                    </div>
                  </div>

                  {/* Stack Tecnológico */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">
                      Tecnologías Implementadas
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {selectedImage.technologies.map((tech, i) => (
                        <span key={i} className="bg-slate-900 text-white text-sm px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                          <Code2 className="w-4 h-4 text-emerald-400" />
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Navegación del Modal */}
                  <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-200">
                    <button
                      onClick={prevImage}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors disabled:opacity-50"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      <span className="hidden sm:inline">Anterior</span>
                    </button>
                    
                    <button
                      onClick={nextImage}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    >
                      <span className="hidden sm:inline">Siguiente</span>
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Estilo para scrollbar personalizado en el modal */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 9999px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </section>
  );
};

export default Gallery;